import { WikiLayout } from "@/components/WikiLayout";
import { WikiPage } from "@/components/WikiPage";

const Forms = () => {
  return (
    <WikiLayout>
      <WikiPage 
        title="Forms & Documents" 
        description="Downloadable forms and required documentation"
      >
        <section className="space-y-6">
          <p>
            This page provides access to common forms and documents you'll need as a BlueLux independent contractor. 
            All forms are available digitally through the contractor portal.
          </p>

          <div className="bg-primary/10 border border-primary/20 rounded-lg p-6">
            <p className="font-medium mb-2">📋 Access Contractor Portal</p>
            <p className="text-sm text-muted-foreground">
              All forms are available in the contractor portal at: portal.bluelux.com
            </p>
          </div>

          <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">Daily Forms</h2>
          <div className="space-y-4">
            <div className="border border-border rounded-lg p-4 hover:border-primary transition-colors">
              <h3 className="text-lg font-medium mb-2">Service Report Form</h3>
              <p className="text-sm text-muted-foreground mb-2">
                Complete for each pool service stop. Documents water chemistry, services performed, 
                and any issues found.
              </p>
              <p className="text-xs text-muted-foreground italic">
                Usually submitted digitally via mobile app, but paper backup available if needed.
              </p>
            </div>

            <div className="border border-border rounded-lg p-4 hover:border-primary transition-colors">
              <h3 className="text-lg font-medium mb-2">Customer Communication Log</h3>
              <p className="text-sm text-muted-foreground mb-2">
                Document all customer interactions, requests, complaints, or special instructions.
              </p>
              <p className="text-xs text-muted-foreground italic">
                Required when customer has concerns or makes special requests.
              </p>
            </div>

            <div className="border border-border rounded-lg p-4 hover:border-primary transition-colors">
              <h3 className="text-lg font-medium mb-2">Time Sheet</h3>
              <p className="text-sm text-muted-foreground mb-2">
                Track your daily route start/end times and stops completed. Submit weekly.
              </p>
              <p className="text-xs text-muted-foreground italic">
                Auto-tracked in mobile app, but manual form available for backup.
              </p>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">Incident & Safety Forms</h2>
          <div className="space-y-4">
            <div className="border border-border rounded-lg p-4 hover:border-primary transition-colors">
              <h3 className="text-lg font-medium mb-2">Incident Report Form</h3>
              <p className="text-sm text-muted-foreground mb-2">
                <strong className="text-destructive">Required within 24 hours</strong> of any accident, 
                injury, property damage, or safety incident.
              </p>
              <p className="text-xs text-muted-foreground">
                Include: Date, time, location, detailed description, witnesses, photos if applicable, 
                immediate actions taken.
              </p>
            </div>

            <div className="border border-border rounded-lg p-4 hover:border-primary transition-colors">
              <h3 className="text-lg font-medium mb-2">Vehicle Accident Report</h3>
              <p className="text-sm text-muted-foreground mb-2">
                Complete immediately after any vehicle accident, regardless of fault or damage level.
              </p>
              <p className="text-xs text-muted-foreground">
                Include: Police report number, other driver info, witness statements, photos, insurance details.
              </p>
            </div>

            <div className="border border-border rounded-lg p-4 hover:border-primary transition-colors">
              <h3 className="text-lg font-medium mb-2">Near-Miss Report</h3>
              <p className="text-sm text-muted-foreground mb-2">
                Document close calls or potential hazards. Helps prevent future incidents. 
                Does NOT count against your safety record.
              </p>
              <p className="text-xs text-muted-foreground">
                Encouraged—helps identify and fix safety issues before accidents happen.
              </p>
            </div>

            <div className="border border-border rounded-lg p-4 hover:border-primary transition-colors">
              <h3 className="text-lg font-medium mb-2">Chemical Spill Report</h3>
              <p className="text-sm text-muted-foreground mb-2">
                Document any chemical spills, leaks, or exposure incidents. Required even for minor spills.
              </p>
            </div>

            <div className="border border-border rounded-lg p-4 hover:border-primary transition-colors">
              <h3 className="text-lg font-medium mb-2">Workers' Compensation Claim Form</h3>
              <p className="text-sm text-muted-foreground mb-2">
                Complete if you're injured on the job. Submit immediately to HR along with medical documentation.
              </p>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">Customer Service Forms</h2>
          <div className="space-y-4">
            <div className="border border-border rounded-lg p-4 hover:border-primary transition-colors">
              <h3 className="text-lg font-medium mb-2">New Customer Setup Form</h3>
              <p className="text-sm text-muted-foreground mb-2">
                Initial pool assessment for new customers. Document pool type, equipment, access instructions, 
                customer preferences.
              </p>
            </div>

            <div className="border border-border rounded-lg p-4 hover:border-primary transition-colors">
              <h3 className="text-lg font-medium mb-2">Equipment Repair Recommendation</h3>
              <p className="text-sm text-muted-foreground mb-2">
                Document needed repairs, provide estimate, get customer approval. Required for all repair work.
              </p>
            </div>

            <div className="border border-border rounded-lg p-4 hover:border-primary transition-colors">
              <h3 className="text-lg font-medium mb-2">Customer Referral Form</h3>
              <p className="text-sm text-muted-foreground mb-2">
                Submit when current customer refers a new customer. Ensures you receive referral bonus.
              </p>
            </div>

            <div className="border border-border rounded-lg p-4 hover:border-primary transition-colors">
              <h3 className="text-lg font-medium mb-2">Service Complaint Resolution</h3>
              <p className="text-sm text-muted-foreground mb-2">
                Document customer complaints, your response, resolution steps taken, outcome.
              </p>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">HR & Administrative Forms</h2>
          <div className="space-y-4">
            <div className="border border-border rounded-lg p-4 hover:border-primary transition-colors">
              <h3 className="text-lg font-medium mb-2">Time Off Request Form</h3>
              <p className="text-sm text-muted-foreground mb-2">
                Request PTO, vacation, or unpaid time off. Submit with required advance notice.
              </p>
            </div>

            <div className="border border-border rounded-lg p-4 hover:border-primary transition-colors">
              <h3 className="text-lg font-medium mb-2">Schedule Change Request</h3>
              <p className="text-sm text-muted-foreground mb-2">
                Request permanent changes to your work schedule or route assignment.
              </p>
            </div>

            <div className="border border-border rounded-lg p-4 hover:border-primary transition-colors">
              <h3 className="text-lg font-medium mb-2">Direct Deposit Authorization</h3>
              <p className="text-sm text-muted-foreground mb-2">
                Set up or change direct deposit information. Requires voided check or bank letter.
              </p>
            </div>

            <div className="border border-border rounded-lg p-4 hover:border-primary transition-colors">
              <h3 className="text-lg font-medium mb-2">W-4 Tax Withholding Form</h3>
              <p className="text-sm text-muted-foreground mb-2">
                Update federal tax withholding. Submit to HR for processing.
              </p>
            </div>

            <div className="border border-border rounded-lg p-4 hover:border-primary transition-colors">
              <h3 className="text-lg font-medium mb-2">Emergency Contact Update</h3>
              <p className="text-sm text-muted-foreground mb-2">
                Update emergency contact information. Keep this current!
              </p>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">Equipment & Inventory Forms</h2>
          <div className="space-y-4">
            <div className="border border-border rounded-lg p-4 hover:border-primary transition-colors">
              <h3 className="text-lg font-medium mb-2">Equipment Request/Replacement Form</h3>
              <p className="text-sm text-muted-foreground mb-2">
                Request new equipment or replacement of damaged/worn items.
              </p>
            </div>

            <div className="border border-border rounded-lg p-4 hover:border-primary transition-colors">
              <h3 className="text-lg font-medium mb-2">Chemical Inventory Report</h3>
              <p className="text-sm text-muted-foreground mb-2">
                Weekly check-in of chemical inventory levels. Helps office plan restocking.
              </p>
            </div>

            <div className="border border-border rounded-lg p-4 hover:border-primary transition-colors">
              <h3 className="text-lg font-medium mb-2">Vehicle Maintenance Log</h3>
              <p className="text-sm text-muted-foreground mb-2">
                Track vehicle maintenance, repairs, oil changes. Required for vehicle allowance reimbursement.
              </p>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">Form Submission Guidelines</h2>
          <div className="bg-muted/50 p-6 rounded-lg space-y-4">
            <div>
              <h3 className="text-sm font-medium mb-2">Digital Submission (Preferred)</h3>
              <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground ml-4">
                <li>Upload completed forms to employee portal</li>
                <li>Email to forms@bluelux.com with form name in subject line</li>
                <li>Use mobile app for service reports and incident reports</li>
              </ul>
            </div>
            <div>
              <h3 className="text-sm font-medium mb-2">Paper Submission (If Needed)</h3>
              <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground ml-4">
                <li>Drop off at main office during business hours</li>
                <li>Mail to: BlueLux Pool Service, 1234 Pool Way, San Diego, CA 92101</li>
                <li>Fax: (619) 555-0101</li>
              </ul>
            </div>
            <div>
              <h3 className="text-sm font-medium mb-2">Urgent Forms</h3>
              <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground ml-4">
                <li>Incident reports: Submit within 24 hours</li>
                <li>Vehicle accidents: Submit same day if possible</li>
                <li>Workers' comp: Submit immediately</li>
                <li>Customer complaints: Submit within 24 hours</li>
              </ul>
            </div>
          </div>

          <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 mt-8">
            <p className="text-sm">
              <strong>💡 Need Help?</strong> If you can't find a form or have questions about completing one, 
              contact the office at (619) 555-0100 or email forms@bluelux.com
            </p>
          </div>
        </section>
      </WikiPage>
    </WikiLayout>
  );
};

export default Forms;